import React from 'react';
import { useParams } from 'react-router-dom';
import { useResponsive, useToast, useOnlineStatus } from '@parsina/framework';

import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useChat } from '../../hooks/useChat.js';
import { useCase } from '../../hooks/useCase.js';
import { useMe } from '../../hooks/useMe.js';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { patientName } from './Chat.shared.js';
import ChatWeb from './Chat.web.jsx';
import ChatMobile from './Chat.mobile.jsx';

// «گفتگو با دستیار» — the ChatGPT-shaped page. core.md §9.
//
// ── Why the controller is HERE and not in each surface ───────────────────
//
// The desktop and the phone are two chromes over one conversation: a sidebar
// beside the column, or the same sidebar in a drawer behind a hamburger. Every
// piece of BEHAVIOUR — what a send does, what an answer that arrives after the
// student walked away does, what a deleted thread does — is identical, and it is
// exactly the part where a fork silently drifts. So this file owns the state and
// hands both surfaces the same object.
//
// ── The three navigations the student did not ask for ────────────────────
//
// All three REPLACE the history entry rather than pushing one, because a back
// button that returns to a URL the app immediately corrects again is a trap the
// student cannot leave by going back:
//
//   * the first question of a fresh conversation creates a thread → the URL
//     becomes `/patients/<case>/chat/<threadId>` (core.md §9.5);
//   * the thread in the URL is gone (deleted here or in another tab) → the id
//     comes off the URL and the page becomes a fresh conversation, with the
//     question preserved in the composer;
//   * «گفتگوی جدید» → the same, deliberately.
//
// ── What this screen may never do ────────────────────────────────────────
//
// Write to the record. It reads the patient (for the name and the badge) and it
// writes its own transcript through `askAboutCase`; there is no path from here
// to a history entry, a diagnosis or an order. The AI is a teacher and it never
// writes (core.md §3.4).

export default function ChatScreen() {
  const { caseId, threadId } = useParams();
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useAppNavigate();
  const online = useOnlineStatus();

  const { caseRecord, loading: caseLoading, error: caseError, reload: reloadCase } = useCase(caseId);
  const { balance } = useMe();

  // Bumped after every send; the composer focuses itself when it changes (§9.6).
  const [focusSignal, setFocusSignal] = React.useState(0);

  const onThreadCreated = React.useCallback((id) => {
    navigate('chat', { caseId, threadId: id, replace: true });
  }, [navigate, caseId]);

  // The id comes off the URL either way; the toast «گفتگو پیدا نشد» (core.md
  // §7.6) is for a thread that is unknown, not this student's, or deleted in
  // another tab — NOT for one the student just deleted here, which already
  // toasts «گفتگو حذف شد».
  const onThreadGone = React.useCallback((reason) => {
    navigate('chat', { caseId, replace: true });
    if (reason === 'missing') toast.error(t('chat.thread.notFoundToast'));
  }, [navigate, caseId, toast, t]);

  const onMoved = React.useCallback(() => {
    toast.success(t('chat.thread.movedToast'));
  }, [toast, t]);

  const chat = useChat({
    caseId,
    threadId: threadId || null,
    onThreadCreated,
    onThreadGone,
    onMoved,
  });

  const name = patientName(caseRecord, t('chat.header.patient'));

  const send = React.useCallback(async (text) => {
    const ok = await chat.send(text);
    setFocusSignal((n) => n + 1);
    return ok;
  }, [chat]);

  const openThread = React.useCallback((id) => {
    navigate('chat', { caseId, threadId: id });
  }, [navigate, caseId]);

  const startNew = React.useCallback(() => {
    chat.startNew();
    navigate('chat', { caseId, replace: true });
  }, [chat, navigate, caseId]);

  const rename = React.useCallback(async (id, title) => {
    const row = await chat.rename(id, title);
    if (row) toast.success(t('chat.thread.renamed'));
    return row;
  }, [chat, toast, t]);

  const remove = React.useCallback(async (id) => {
    await chat.remove(id);
    toast.success(t('chat.thread.deleted'));
  }, [chat, toast, t]);

  const props = {
    caseId,
    threadId: threadId || null,
    name,
    caseRecord,
    caseLoading,
    caseError,
    reloadCase,
    balance,
    chat,
    send,
    openThread,
    startNew,
    rename,
    remove,
    focusSignal,
    // `useOnlineStatus` returns an OBJECT, never a string — a truthiness check on
    // it is always true, which is how a sibling app shipped a permanently
    // "online" banner.
    offline: Boolean(online?.isOffline),
    onBack: () => navigate('patient', { caseId }),
    onTopUp: () => navigate('credits'),
    onSwitchPatient: () => navigate('patients'),
    onOpenVisits: () => navigate('patient', { caseId, tab: 'visits' }),
  };

  return isMobile ? <ChatMobile {...props} /> : <ChatWeb {...props} />;
}
